import React, { useState, useEffect, useCallback } from 'react';
import { useAdminService } from '../hooks/useAdminService';
import './Admin.css';

const Admin = () => {
  const adminService = useAdminService();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({});

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    roles: ['user']
  });

  // Edit states
  const [editingUser, setEditingUser] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({ password: '' });

  const ROLE_OPTIONS = ['user', 'admin'];

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminService.getUsers(currentPage, 20, searchTerm);
      setUsers(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
      console.error('Load users error:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  // Load users on mount and when currentPage or searchTerm changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateUserChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'roles') {
      const updatedRoles = checked
        ? [...newUser.roles, value]
        : newUser.roles.filter(r => r !== value);
      setNewUser({ ...newUser, roles: updatedRoles });
    } else {
      setNewUser({ ...newUser, [name]: value });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      if (!newUser.email || !newUser.password) {
        setError('Email and password are required');
        return;
      }

      if (newUser.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      await adminService.createUser(newUser);
      setSuccess('User created successfully!');
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        roles: ['user']
      });
      setShowCreateForm(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
      console.error('Create user error:', err);
    }
  };

  const handleEditUserClick = (user) => {
    setEditingUser(user);
    setEditRoles(user.roles || []);
  };

  const handleRoleChange = (role, checked) => {
    if (checked) {
      setEditRoles([...editRoles, role]);
    } else {
      setEditRoles(editRoles.filter(r => r !== role));
    }
  };

  const handleUpdateRoles = async () => {
    try {
      setError('');
      setSuccess('');

      if (editRoles.length === 0) {
        setError('User must have at least one role');
        return;
      }

      await adminService.updateUserRoles(editingUser.id, editRoles);
      setSuccess('User roles updated successfully!');
      setEditingUser(null);
      setEditRoles([]);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update roles');
      console.error('Update roles error:', err);
    }
  };

  const handleOpenPasswordModal = (user) => {
    setEditingUser(user);
    setPasswordFormData({ password: '' });
    setShowPasswordModal(true);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      if (!passwordFormData.password || passwordFormData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      await adminService.setUserPassword(editingUser.id, passwordFormData.password);
      setSuccess('Password updated successfully!');
      setShowPasswordModal(false);
      setPasswordFormData({ password: '' });
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set password');
      console.error('Set password error:', err);
    }
  };

  const handleReset2FA = async (userId) => {
    if (window.confirm('Are you sure you want to reset this user\'s 2FA? They will need to set it up again on next login.')) {
      try {
        setError('');
        setSuccess('');
        await adminService.reset2FA(userId);
        setSuccess('2FA reset successfully!');
        loadUsers();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to reset 2FA');
        console.error('Reset 2FA error:', err);
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        setError('');
        setSuccess('');
        await adminService.deleteUser(userId);
        setSuccess('User deleted successfully!');
        loadUsers();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
        console.error('Delete user error:', err);
      }
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      setError('');
      setSuccess('');
      await adminService.restoreUser(userId);
      setSuccess('User restored successfully!');
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore user');
      console.error('Restore user error:', err);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>User Management</h1>
        <p>Create, manage, and configure user accounts and permissions</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="admin-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by email, first name, or last name..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create New User'}
        </button>
      </div>

      {showCreateForm && (
        <div className="new-user-form">
          <div className="form-section">
            <h2>Create New User</h2>
            <form onSubmit={handleCreateUser} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={newUser.email}
                    onChange={handleCreateUserChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={newUser.first_name}
                    onChange={handleCreateUserChange}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={newUser.last_name}
                    onChange={handleCreateUserChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Password * (minimum 8 characters)</label>
                  <input
                    type="password"
                    name="password"
                    value={newUser.password}
                    onChange={handleCreateUserChange}
                    required
                    minLength="8"
                  />
                </div>

                <div className="form-group roles-group">
                  <label>Roles</label>
                  <div className="checkbox-group">
                    {ROLE_OPTIONS.map((role) => (
                      <label key={role} className="checkbox-label">
                        <input
                          type="checkbox"
                          name="roles"
                          value={role}
                          checked={newUser.roles.includes(role)}
                          onChange={handleCreateUserChange}
                        />
                        {role}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-section">
        <h2>All Users ({pagination.total || 0})</h2>
        
        {loading ? (
          <div className="loading">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="no-results">No users found</div>
        ) : (
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Roles</th>
                  <th>2FA</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className={user.deleted_at ? 'deleted' : ''}>
                    <td className="email-cell">{user.email}</td>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>
                      <div className="roles-badges">
                        {user.roles && user.roles.map((role) => (
                          <span key={role} className={`badge badge-${role}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${user.is_2fa_enabled ? 'badge-success' : 'badge-warning'}`}>
                        {user.is_2fa_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="date-cell">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="date-cell">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="btn btn-small btn-info"
                          onClick={() => handleEditUserClick(user)}
                          title="Edit roles"
                        >
                          Roles
                        </button>
                        <button
                          className="btn btn-small btn-warning"
                          onClick={() => handleOpenPasswordModal(user)}
                          title="Set password"
                        >
                          Password
                        </button>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleReset2FA(user.id)}
                          title="Reset 2FA"
                        >
                          Reset 2FA
                        </button>
                        {!user.deleted_at && (
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete user"
                          >
                            Delete
                          </button>
                        )}
                        {user.deleted_at && (
                          <button
                            className="btn btn-small btn-success"
                            onClick={() => handleRestoreUser(user.id)}
                            title="Restore user"
                          >
                            Restore
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn btn-small"
            >
              Previous
            </button>
            <span className="page-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
              disabled={currentPage === pagination.pages}
              className="btn btn-small"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Edit Roles Modal */}
      {editingUser && editingUser.id && !showPasswordModal && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Roles for {editingUser.email}</h2>
              <button className="close-btn" onClick={() => setEditingUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="checkbox-group">
                {ROLE_OPTIONS.map((role) => (
                  <label key={role} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editRoles.includes(role)}
                      onChange={(e) => handleRoleChange(role, e.target.checked)}
                    />
                    {role}
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdateRoles}>
                Update Roles
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Set Password for {editingUser.email}</h2>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <form onSubmit={handleSetPassword}>
              <div className="modal-body">
                <div className="form-group">
                  <label>New Password * (minimum 8 characters)</label>
                  <input
                    type="password"
                    value={passwordFormData.password}
                    onChange={(e) => setPasswordFormData({ password: e.target.value })}
                    required
                    minLength="8"
                    autoFocus
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Set Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
